import { ALL_LNGS_NAMESPACES, LNGS_NAMESPACE_LOGICAL_DEVICE } from '@ingenico/util-i18n-config';
import { Banner } from '@ingenico/react-ui/banner';
import { BasicDialogModal } from '@ingenico/react-ui/basic-dialog-modal';
import { CoreAlert } from '@ingenico/react-ui/core-alert';
import { CoreButton } from '@ingenico/react-ui/core-button';
import { CoreControllerInput } from '@ingenico/react-ui/core-form';
import { CoreTypography, CoreTypographyVariant } from '@ingenico/react-ui/core-typography';
import { SwitchBlock } from '@ingenico/react-ui/form-section';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { CreateLogRequestModalProps } from './CreateLogRequestModal.model';
import styles from './CreateLogRequestModal.module.scss';
import { collectFromOptionsArray } from './hooks/useFormularyOfCreateLogRequest.hook';
import { useCreateLogRequestModalVM } from './useCreateLogRequestModal.vm';

export function CreateLogRequestModal({
  isOpen,
  isLoading,
  onSubmit,
  onCancel,
  logicalDeviceId,
  errorMessage: errorMessageProp,
}: CreateLogRequestModalProps): ReactNode {
  const { t } = useTranslation(Object.values(LNGS_NAMESPACE_LOGICAL_DEVICE));

  const { form, errorMessage, logRequestState, handleCancel, onCreateLogSubmission, isError } =
    useCreateLogRequestModalVM({
      isOpen,
      logicalDeviceId,
      onCancel,
      onSubmit,
      errorMessage: errorMessageProp,
    });

  return (
    <BasicDialogModal
      actions={
        <div className={styles.modalActions}>
          <CoreButton disabled={isLoading || logRequestState.isLoading} onClick={handleCancel} variant="text">
            {t('button.cancel', { ns: ALL_LNGS_NAMESPACES.COMMON })}
          </CoreButton>
          <CoreButton
            disabled={!form?.formState?.isValid}
            loading={isLoading || logRequestState.isLoading}
            onClick={onCreateLogSubmission as () => void}
            variant="contained"
          >
            {t('button.createLogs', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
          </CoreButton>
        </div>
      }
      customPaperClassName={styles.paperContainer}
      customActionsClassName={styles.customActionsClassName}
      hasCloseIcon
      hasDivider
      isOpen={isOpen}
      onClose={handleCancel}
      title={t('logcat.creation.modal.title', { ns: ALL_LNGS_NAMESPACES.LOGICAL_DEVICE })}
    >
      <div className={styles.modalContent}>
        {isError && <Banner message={errorMessage} />}
        <CoreAlert
          severity={'Info'}
          title={t('logcat.creation.alert.title')}
          message={t('logcat.creation.alert.message')}
        />

        <div>
          <CoreTypography variant={CoreTypographyVariant.Body02Medium}>
            {t('logcat.creation.formularyFields.collectLogFrom')}
          </CoreTypography>
          <div className={classNames(styles.switchOptionsContainer)}>
            <Controller
              control={form.control}
              name="collectFrom"
              render={({ field }) => {
                const { value, onChange } = field;
                return (
                  <>
                    {collectFromOptionsArray.map((option) => {
                      return (
                        <SwitchBlock
                          key={option}
                          boxContainerClassName={classNames(styles.switchContainer)}
                          label={t(`logcat.creation.formularyFields.collectLogFromOptions.${option}`)}
                          checked={value.includes(option)}
                          onChange={(event, _checked) => {
                            const filteredValue = value.filter((el) => el !== option);
                            if (event.target.checked) {
                              filteredValue.unshift(option);
                            }
                            onChange(filteredValue);
                          }}
                        />
                      );
                    })}
                  </>
                );
              }}
            />
          </div>
        </div>
        <CoreControllerInput
          control={form.control}
          dataTestId="field-create-group-name"
          label={t('logcat.creation.formularyFields.title')}
          name="title"
          maxLength={50}
          required
        />
        <CoreControllerInput
          control={form.control}
          dataTestId="field-create-group-description"
          label={t('logcat.creation.formularyFields.description')}
          maxLength={200}
          multiline
          name="description"
        />
        <CoreControllerInput
          control={form.control}
          dataTestId="field-create-group-name"
          label={t('logcat.creation.formularyFields.duration')}
          name="durationHour"
          required
        />
      </div>
    </BasicDialogModal>
  );
}
